// packages/shared/api-client/endpoints/departments.js
// Department management API endpoints

import { baseApi } from "../baseApi";

/**
 * Department API endpoints
 * Provides department management for fire departments
 */
const departmentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all departments (authenticated)
    getDepartments: builder.query({
      query: () => '/api/departments',
      transformResponse: (response) => {
        // Backend returns { status: 'success', data: { departments: [...] } }
        // Transform to { status: 'success', data: [...] } for consistency
        if (response?.data?.departments) {
          return {
            ...response,
            data: response.data.departments,
          };
        }
        return response;
      },
      providesTags: (result) =>
        result?.data && Array.isArray(result.data)
          ? [
              ...result.data.map(({ id }) => ({ type: 'Department', id })),
              { type: 'Department', id: 'LIST' },
            ]
          : [{ type: 'Department', id: 'LIST' }],
    }),

    // Get all departments by registration code (public, for registration flow)
    getDepartmentsByCode: builder.query({
      query: (code) => `/api/departments/by-code/${code}`,
      transformResponse: (response) => {
        // Backend returns { status: 'success', data: { departments: [...] } }
        // Transform to { status: 'success', data: [...] } for consistency
        if (response?.data?.departments) {
          return {
            ...response,
            data: response.data.departments,
          };
        }
        return response;
      },
      providesTags: (result) =>
        result?.data && Array.isArray(result.data)
          ? [
              ...result.data.map(({ id }) => ({ type: 'Department', id })),
              { type: 'Department', id: 'LIST' },
            ]
          : [{ type: 'Department', id: 'LIST' }],
    }),

    // Get specific department by ID
    getDepartmentById: builder.query({
      query: (id) => `/api/departments/${id}`,
      providesTags: (result, error, id) => [{ type: 'Department', id }],
    }),

    // Admin: Create new department
    createDepartment: builder.mutation({
      query: (departmentData) => ({
        url: '/api/departments',
        method: 'POST',
        body: departmentData, // { name, ranks? }
      }),
      invalidatesTags: [{ type: 'Department', id: 'LIST' }],
    }),

    // Admin: Update department
    updateDepartment: builder.mutation({
      query: ({ id, ...departmentData }) => ({
        url: `/api/departments/${id}`,
        method: 'PUT',
        body: departmentData, // { name?, ranks? }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Department', id },
        { type: 'Department', id: 'LIST' },
      ],
    }),

    // Admin: Delete department
    deleteDepartment: builder.mutation({
      query: (id) => ({
        url: `/api/departments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Department', id: 'LIST' }],
    }),
  }),
});

// Export hooks for usage in components
export const useGetDepartmentsQuery = departmentApi.useGetDepartmentsQuery;
export const useGetDepartmentsByCodeQuery = departmentApi.useGetDepartmentsByCodeQuery;
export const useGetDepartmentByIdQuery = departmentApi.useGetDepartmentByIdQuery;
export const useCreateDepartmentMutation = departmentApi.useCreateDepartmentMutation;
export const useUpdateDepartmentMutation = departmentApi.useUpdateDepartmentMutation;
export const useDeleteDepartmentMutation = departmentApi.useDeleteDepartmentMutation;
